<template>

    <div class="col-12 col-sm-3 col-md-4 col-lg-12">

        <div class="profile-card row">
            <div class="col-4">
                <div class="col-12">
                    <div class="patient-action">
                        <label for="patient" class="form-label">Patient Name</label>
                        <div>
                            <input
                                v-model="patientSerialNo"
                                name="serial"
                                type="number"
                                class="form-control"
                                placeholder="Patient ID"
                                @keyup="searchPatientById"
                            />
                        </div>
                        <button 
                            class="btn btn-primary btn-sm"
                            @click="openPatientAddModal"
                        >
                            <i class="fas fa-plus"></i>
                            Patient
                        </button>
                    </div>

                    <AsyncSelect
                        placeholder="Patient Name, ID, Mobile No"
                        v-model="patient"
                        :api-service="fetchContactProfiles"
                        :additional-query="{ type: 'patient'}"
                        :format-label="formatPatientLabel"
                        :reduce="option => option.id"
                        @option:selected="onSelectPatient"
                        :additional-option="additionalOption"
                        :disabled="Boolean(isDisabled)"
                    />
                </div>
            </div>

            <div class="col-8 form-margin">
                <div v-if="userInfo" class="row">
                    <div class="mt-20  col-12 col-sm-6 col-md-6 col-lg-2">
                        <label class="form-label" for="Sex">Sex</label>
                        <input
                            :value="userInfo.gender"
                            type="text"
                            class="form-control capitalize"
                            readonly
                        />
                    </div>

                    <div class="mt-20 col-12 col-sm-6 col-md-6 col-lg-3">
                        <label class="form-label" for="Age">Age</label>
                        <input
                            :value="calculateAge(userInfo.birthday)"
                            type="text"
                            class="form-control capitalize"
                            readonly
                        />
                    </div>

                    <div class="mt-20 col-12 col-sm-6 col-md-6 col-lg-2">
                        <label class="form-label" for="Contact No">Contact No</label>
                        <input
                            :value="userInfo.mobile_no"
                            type="text"
                            class="form-control"
                            readonly
                        />
                    </div>

                    <div class="mt-20 col-12 col-sm-6 col-md-6 col-lg-2">
                        <label class="form-label">Serial</label>
                        <input
                            :value="userInfo.serial_no"
                            type="text"
                            class="form-control"
                            readonly
                        />
                    </div>

                    <div class="mt-20 col-12 col-sm-6 col-md-6 col-lg-3">
                        <label class="form-label">Address</label>
                        <input
                            :value="userInfo.full_address"
                            type="text"
                            class="form-control"
                            readonly
                        />
                    </div>

                    <div v-if="userInfo.guardian_name" class="mt-20 col-12 col-sm-6 col-md-6 col-lg-3">
                        <label class="form-label">Guardian Name</label>
                        <input
                            :value="userInfo.guardian_name"
                            type="text"
                            class="form-control capitalize"
                            readonly
                        />
                    </div>

                    <div v-if="userInfo.guardian_mobile_no" class="mt-20 col-12 col-sm-6 col-md-6 col-lg-3">
                        <label class="form-label">Guardian Mobile No</label>
                        <input
                            :value="userInfo.guardian_mobile_no"
                            type="text"
                            class="form-control capitalize"
                            readonly
                        />
                    </div>

                    <div v-if="userInfo.guardian_relationship" class="mt-20 col-12 col-sm-6 col-md-6 col-lg-3">
                        <label class="form-label">Guardian RelationShip</label>
                        <input
                            :value="userInfo.guardian_relationship"
                            type="text"
                            class="form-control capitalize"
                            readonly
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="col-3 px-1">
            
        </div>

        <AddPatientModal 
            v-if="$store.state.isModalOpenTwo"
            :doctors="doctors"
            @onCreateProfile="onCreateProfile"
        />

        <Loader v-if="isLoader" />
        
    </div>

</template>

<script setup>
import handleHospital from '@/services/modules/hospital'
import AddPatientModal from '@/components/molecule/company/hospital/AddPatientModal'
import { inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import handleContact from '@/services/modules/contact';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import Loader from "@/components/atom/LoaderComponent";

const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchContactProfiles } = handleContact();
const $store = useStore();
const props = defineProps(['patients', 'doctors', 'profileId', 'isDisabled']);
const emit = defineEmits(['update:profileId']);
const $route = useRoute();
const userInfo = ref(null);
const additionalOption = ref([]);
const patient = ref(null);
const patientSerialNo = ref(null);
const isLoader = ref(false);
const showError = inject('showError');

const {
    fetchPatientDetails,
    fetchPatientDetailsBySerial,
    calculateAge
} = handleHospital()

function getQuery() {
    let query = `?company_id=${$route.params.companyId}`;
    return query;
}

const onCreateProfile = async(profileInfo) => {
    userInfo.value = profileInfo;
    let newPatient = {
        id: profileInfo.id,
        name: profileInfo.full_name
    };
    additionalOption.value.push(newPatient)
    emit('update:profileId', profileInfo.id);
    await getPatientDetails(profileInfo.id);
}

const onSelectPatient = async (value) => {
    emit('update:profileId', value.id);
    await getPatientDetails(value.id);
}

const searchPatientById = async () => {
    if(!patientSerialNo.value) return;

    userInfo.value = null
    patient.value = null
    isLoader.value = true;
    const query = getQuery();

    await fetchPatientDetailsBySerial(query, patientSerialNo.value)
        .then((res) => {
            
            if (!res.status) {
                return showError(res.message)
            }
            
            userInfo.value = res.data
            patient.value = {
                id: userInfo.value.id,
                name: userInfo.value.full_name
            }

            emit('update:profileId', userInfo.value.id);
        })
    
    isLoader.value = false;
}

const getPatientDetails = async (id) => {
    const query = getQuery();
    await fetchPatientDetails(query, id)
        .then((res) => {
            if(!res.status) return;
            
            userInfo.value = res.data
            patient.value = {
                id: userInfo.value.id,
                name: userInfo.value.full_name
            }
        })
}

const openPatientAddModal = () => {
    $store.state.isModalOpenTwo = true
}

onMounted(async() => {
    if(props.profileId){
        await getPatientDetails(props.profileId);
    }
})
</script>
<style scoped>
.single-search-action{
    margin-top: 11%;
}
.patient-search{
    width: 76%;
    margin: 10px;
}
.patient-action {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.admission-history hr{
    margin: 4px;
}
.profile-tale > :not(caption) > * > * {
    padding: 0.5rem 0.5rem !important;
}
.profile-tale{
    font-weight: bold;
}

.profile-tale > :not(caption) > * > * {
    border-bottom-width: 0;
}

.profile-tale tr {
    border: none;
}
.profile-img {
    text-align: center;
}

.profile-img img {
    width: 50%;
}
.profile-card {
    margin-top: 10px;
    margin-right: 10px;
    margin-left: 10px;
}
.profile-head{
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.profile-card .input-group{
    width: 90%;
}
.mt-20 {
    margin-top: 20px;
}
</style>